<template>
    <!-- Single root on purpose: SharpenedReadingsStep passes class="readings" down, and a
         fragment root would drop it along with the parent's scoped rule. The sr-only region is
         absolutely positioned, so the list is still the only element in flow here. -->
    <div>
        <!-- One region for the whole list, mounted with it and rewritten as each insight lands.
             A region per pending slot would be created by the v-for with its text already inside,
             which never announces, and three of them would talk over each other if they did. -->
        <p class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ pendingAnnouncement }}</p>
        <ol class="readings">
            <li v-for="reading in readings" :key="reading.id">
                <ReadingCard :reading="reading" :show-rating="showRating" :location="location" />
            </li>
            <li v-for="slot in pendingCount" :key="`pending-${slot}`">
                <ReadingSkeletonCard />
                <span class="pending">{{ t("onboarding.calendarFirst.readingList.pending", { number: readings.length + slot }) }}</span>
            </li>
        </ol>
    </div>
</template>

<script setup lang="ts">
/**
 * Reuse My Work's insight presentation so users recognize the same objects later. Show all readings: there
 * are at most three, and the previous carousel hid two.
 */
import ReadingCard from "~vue/CalendarFirstOnboarding/ReadingCard.vue";
import ReadingSkeletonCard from "~vue/CalendarFirstOnboarding/ReadingSkeletonCard.vue";
import type { OnboardingReading } from "~vue/CalendarFirstOnboarding/types";
import type { LabInsightLocation } from "~vue/types/labs";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const {
    readings,
    pendingCount = 0,
    showRating = true,
    location = "onboarding",
} = defineProps<{
    readings: OnboardingReading[];
    pendingCount?: number;
    /** Offer the rating bar on each card. */
    showRating?: boolean;
    /** Which setup entry point is rendering the list. */
    location?: LabInsightLocation;
}>();

const { t } = useI18n();

const pendingAnnouncement = computed(() =>
    pendingCount > 0 ? t("onboarding.calendarFirst.readingList.pendingAnnouncement", { number: readings.length + 1, total: readings.length + pendingCount }) : "",
);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .readings {
        @apply flex flex-col gap-2.5;
    }

    .pending {
        @apply body-small mt-3 block text-ink-muted;
    }
}
</style>
